/**
 * Slice a single availability gap into `duration`-sized slots with
 * optional buffer clearance around each. Stops when the next slot
 * would overflow the gap end.
 *
 * Buffer model (opt-in via `beforeMinutes` / `afterMinutes`):
 *   - First slot starts at `startMs + beforeMinutes` (room before).
 *   - Stride between slot-starts = `duration + before + after`.
 *   - Last slot must end ≤ `endMs - afterMinutes`.
 *   - Slot itself stays `duration` minutes wide — buffer is clearance,
 *     not part of the visible block.
 *
 * `beforeMinutes = afterMinutes = 0` reproduces back-to-back behavior,
 * so existing callers don't need to change.
 */
import moment from 'moment-timezone';
import type { SelfSlot } from '../types';

interface SliceArgs {
  /** ms epoch — already rounded/buffered by earlier stages. */
  startMs: number;
  /** ms epoch — end of the usable gap. */
  endMs: number;
  durationMinutes: number;
  timezone: string;
  dateKey: string;
  /** Clearance required before each slot. Default 0. */
  beforeMinutes?: number;
  /** Clearance required after each slot. Default 0. */
  afterMinutes?: number;
}

export function sliceInterval({
  startMs,
  endMs,
  durationMinutes,
  timezone,
  dateKey,
  beforeMinutes = 0,
  afterMinutes = 0,
}: SliceArgs): SelfSlot[] {
  if (durationMinutes <= 0) return [];
  const durationMs = durationMinutes * 60_000;
  const beforeMs = Math.max(0, beforeMinutes) * 60_000;
  const afterMs = Math.max(0, afterMinutes) * 60_000;
  const strideMs = durationMs + beforeMs + afterMs;
  const out: SelfSlot[] = [];
  let cursor = startMs + beforeMs;
  const latestEnd = endMs - afterMs;
  while (cursor + durationMs <= latestEnd) {
    out.push({
      id: `gen-${dateKey}-${cursor}`,
      start: moment.tz(cursor, timezone).toISOString(),
      end: moment.tz(cursor + durationMs, timezone).toISOString(),
      dateKey,
      origin: 'generated',
    });
    cursor += strideMs;
  }
  return out;
}
